import type { TextColor, TextSize, TextUnderline } from './types'

import { cva } from 'class-variance-authority'

// Ancestor-hover/focus is scoped to interactive elements (`a`, `button`) so
// a non-interactive parent (e.g. a flex row containing a sibling label)
// doesn't trigger the color shift on text that isn't actually clickable.
const underlineBase =
  '[text-decoration-thickness:1px] [text-underline-offset:2px] decoration-text-tertiary transition-colors hover:decoration-text-primary focus-visible:decoration-text-primary [a:hover_&]:decoration-text-primary [button:hover_&]:decoration-text-primary [a:focus-visible_&]:decoration-text-primary [button:focus-visible_&]:decoration-text-primary'

const underlineClasses: Record<TextUnderline, string> = {
  none: '',
  dotted: `underline decoration-dotted ${underlineBase}`,
  solid: `underline decoration-solid ${underlineBase}`,
}

const colorClasses: Record<TextColor, string> = {
  accent: 'text-text-accent',
  danger: 'text-text-danger',
  error: 'text-text-danger-weak',
  inherit: '',
  inverse: 'text-text-inverse',
  'nav-options-active': 'text-text-nav-options-active',
  'nav-options-default': 'text-text-nav-options-default',
  'nav-options-section-title': 'text-text-nav-options-section-title',
  neutral: 'text-text-neutral',
  primary: 'text-text-primary',
  processing: 'text-text-processing',
  secondary: 'text-text-secondary',
  special: 'text-text-special',
  success: 'text-text-success',
  tertiary: 'text-text-tertiary',
  warning: 'text-text-warning',
}

/**
 * CVA variants for the Text component
 * Uses typography tokens from typography.css and color tokens from colors.css
 */
export const textVariants = cva('font-sans', {
  defaultVariants: {
    color: 'inherit',
    size: 'lg',
    truncate: false,
    underline: 'none',
    weight: 'regular',
  },
  variants: {
    color: colorClasses,
    size: {
      '2xl': 'text-size-2xl leading-2xl tracking-2xl',
      '3xl': 'text-size-3xl leading-3xl tracking-3xl',
      lg: 'text-size-lg leading-lg tracking-lg',
      md: 'text-size-md leading-md tracking-md',
      sm: 'text-size-sm leading-sm tracking-sm',
      xl: 'text-size-xl leading-xl tracking-xl',
      xs: 'text-size-xs leading-xs tracking-xs',
    },
    truncate: {
      false: '',
      true: 'truncate',
    },
    underline: underlineClasses,
    weight: {
      heavy: 'font-semibold',
      regular: 'font-normal',
      strong: 'font-medium',
    },
  },
})

/**
 * Standalone CVA fragment exposing only the color variant - inherits the
 * parent's size and weight. Use when a wrapping element already governs
 * typography (e.g. `Title.Primary`) and the inner span only needs to recolor
 * a word or phrase.
 */
export const textColorVariants = cva('', {
  variants: { color: colorClasses },
  defaultVariants: { color: 'inherit' },
})

/**
 * Standalone CVA fragment exposing only the underline variant.
 * For places that can't reach for `<Text>` (e.g. third-party renderers, custom elements).
 * @public — underline-only CVA fragment
 */
export const textUnderlineVariants = cva('', {
  variants: { underline: underlineClasses },
  defaultVariants: { underline: 'none' },
})

/**
 * Line-height (leading) per Text size, in rem. Mirrors the `--leading-*`
 * CSS custom properties in `typography.css` and is the source of truth for
 * any code that needs to size something to match a Text line (e.g. the
 * SkeletonText placeholder).
 */
export const TEXT_LEADING_REM: Record<TextSize, number> = {
  xs: 1, // 16px
  sm: 1.125, // 18px
  md: 1.25, // 20px
  lg: 1.375, // 22px
  xl: 1.5, // 24px
  '2xl': 1.75, // 28px
  '3xl': 2, // 32px
}
